import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, Edit2, X, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", content: "", category: "Process", tags: [], active: true };

export default function AdminKBPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/kb").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
      if (editing === "new") await api.post("/admin/kb", payload);
      else await api.put(`/admin/kb/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this KB entry?")) return;
    await api.delete(`/admin/kb/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-kb">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-[#0A1628]">Knowledge base</h1>
        <button
          onClick={() => {
            setEditing("new");
            setForm(EMPTY);
          }}
          data-testid="kb-add"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#0A1628] text-white text-sm font-semibold hover:bg-[#1E293B]"
        >
          <Plus className="w-4 h-4" /> New article
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 soft-shadow mb-5" data-testid="kb-editor">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Title">
              <input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
            <Field label="Category">
              <input className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
          </div>
          <Field label="Content" className="mt-3">
            <textarea rows={6} className="inp resize-none h-auto py-3" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          </Field>
          <Field label="Tags (comma separated)" className="mt-3">
            <input
              className="inp"
              value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
            />
          </Field>
          <div className="mt-4 flex items-center gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-lg border border-[#E5E7EB] text-sm">
              Cancel
            </button>
            <button onClick={save} data-testid="kb-save" className="h-10 px-4 rounded-lg bg-[#0052FF] text-white text-sm font-semibold">
              Save
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-[#E5E7EB] rounded-xl p-4 soft-shadow" data-testid={`kb-item-${it.id.slice(0, 6)}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#0052FF]">{it.category}</div>
                <div className="font-display font-semibold text-[#0A1628] mt-1">{it.title}</div>
                <div className="text-sm text-[#4B5563] mt-1.5 line-clamp-2">{it.content}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {it.tags?.map((t) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#4B5563]">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setEditing(it.id);
                    setForm({ ...it, tags: it.tags?.join(", ") || "" });
                  }}
                  className="w-8 h-8 rounded-md hover:bg-[#F3F4F6] text-[#4B5563]"
                >
                  <Edit2 className="w-3.5 h-3.5 mx-auto" />
                </button>
                <button
                  onClick={() => del(it.id)}
                  className="w-8 h-8 rounded-md hover:bg-red-50 text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5 mx-auto" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .inp { width:100%; height:42px; padding: 0 14px; border-radius: 8px; border: 1px solid #E5E7EB; font-size: 14px; outline: none; }
        .inp:focus { box-shadow: 0 0 0 2px #0052FF; border-color: transparent; }
      `}</style>
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
